import classNames from "classnames";
import React, { Component, FormEvent } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import MaskedInput from "react-text-mask";
import { analyticsFEA } from "src/AnalyticsFEA";
import CarIcon from "src/components/icons/Car";
import { validateEmail, validatePhone, validateRequired } from "src/components/shared/Form/validation";
import TermsAndPrivacy from "src/components/UserAccount/TermsAndPrivacy";
import { getAccountDisclaimer, isUS } from "src/utils/account";
import { isEmpty } from "src/utils/object";
import { AccountState, VehicleState } from "Types/state";
import MessageModal from "../../../../Modal/Components/MessageModal";
import { ReserveVehicleProps } from "./ReserveVehicle";
import "./ReserveVehicleForm.scss";

const TRANSLATION_KEY = "MyGarage.VehicleHeader.ExploreMore.ReserveVehicle.ReserveVehicleForm";
const CHARACTER_LIMIT_FOR_COMMENTS = 255;

export interface ReserveVehicleFormProps {
    account: AccountState;
    CTAText: string;
    dealId: string;
    hasError: boolean;
    onSubmit: (data: any) => Promise<any>;
    onSuccess: () => void;
    region: string;
    values: ReserveVehicleProps["person"];
    vehicle: VehicleState;
}

interface ReserveVehicleFormState {
    formData: FormData;
    error: {
        [index: string]: string;
    };
}

export interface FormData {
    comments: string;
    email: string;
    firstName: string;
    lastName: string;
    optInMarketing?: boolean;
    optInTransactional?: boolean;
    phone: string;
}

class ReserveVehicleForm extends Component<ReserveVehicleFormProps & WithTranslation, ReserveVehicleFormState> {
    constructor(props: ReserveVehicleFormProps & WithTranslation) {
        super(props);
        const { region, values } = props;
        const notUS = !isUS(region);

        this.state = {
            error: {},
            formData: {
                comments: "",
                email: (values && values.email) || "",
                firstName: (values && values.first_name) || "",
                lastName: (values && values.last_name) || "",
                optInMarketing: notUS
                    ? !!(values && values.preferences && values.preferences.opt_in_marketing)
                    : undefined,
                optInTransactional: notUS ? false : undefined,
                phone: (values && values.mobile_phone) || "",
            },
        };
    }

    public onAgreeTransactionalClick = (answer: boolean) => {
        this.setState((prevState: ReserveVehicleFormState) => ({
            formData: {
                ...prevState.formData,
                optInTransactional: answer,
            },
        }));
    };

    public onAgreePromotionalClick = (answer: boolean) => {
        this.setState((prevState: ReserveVehicleFormState) => ({
            formData: {
                ...prevState.formData,
                optInMarketing: answer,
            },
        }));
    };

    public renderFooter = () => {
        return (
            <TermsAndPrivacy
                region={this.props.region}
                account={this.props.account}
                optInTransactional={this.state.formData.optInTransactional}
                optInMarketing={this.state.formData.optInMarketing}
                onAgreeTransactionalClick={this.onAgreeTransactionalClick}
                onAgreePromotionalClick={this.onAgreePromotionalClick}
            />
        );
    };

    public render() {
        const {
            formData: { comments, firstName, lastName, email, phone, optInTransactional },
            error,
        } = this.state;
        const { hasError, region, t, CTAText, account } = this.props;
        const charactersExceed = comments.length > CHARACTER_LIMIT_FOR_COMMENTS;

        const message = getAccountDisclaimer(account)("electric_reserve_vehicle")(`${TRANSLATION_KEY}.subtitle`);

        const messageModalProps = {
            icon: CarIcon,
            logoDisplayed: true,
            message,
            title: CTAText,
            waityButtons: [
                {
                    disabled: (!isUS(region) && !optInTransactional) || charactersExceed,
                    onClick: this.submit,
                    onSuccess: this.props.onSuccess,
                    text: CTAText,
                },
            ],
        };

        return (
            <div className="ReserveVehicleForm">
                <MessageModal {...messageModalProps} renderFooter={this.renderFooter}>
                    <div className="ReserveVehicleForm__form">
                        {hasError && (
                            <div className="ReserveVehicleForm__form-error-message">
                                {t(`${TRANSLATION_KEY}.unableToSubmitMessage`)}
                            </div>
                        )}

                        <div className="ReserveVehicleForm__form-section">
                            <label>{t(`${TRANSLATION_KEY}.whoYouAre`)}</label>
                            <div
                                className={classNames("ReserveVehicleForm__form-section-input", {
                                    error: !!error.firstName,
                                })}
                            >
                                <input
                                    placeholder={t(`${TRANSLATION_KEY}.firstName`)}
                                    type="text"
                                    name="firstName"
                                    value={firstName}
                                    onChange={this.handleInputChange}
                                    onFocus={this.onFocus}
                                />
                                {error.firstName && <label htmlFor="firstName">{error.firstName}</label>}
                            </div>
                            <div
                                className={classNames("ReserveVehicleForm__form-section-input", {
                                    error: !!error.lastName,
                                })}
                            >
                                <input
                                    placeholder={t(`${TRANSLATION_KEY}.lastName`)}
                                    type="text"
                                    name="lastName"
                                    value={lastName}
                                    onChange={this.handleInputChange}
                                    onFocus={this.onFocus}
                                />
                                {error.lastName && <label htmlFor="lastName">{error.lastName}</label>}
                            </div>
                        </div>

                        <div className="ReserveVehicleForm__form-section">
                            <label>{t(`${TRANSLATION_KEY}.contactInformation`)}</label>
                            <div
                                className={classNames("ReserveVehicleForm__form-section-input", {
                                    error: !!error.email,
                                })}
                            >
                                <input
                                    placeholder={t(`${TRANSLATION_KEY}.email`)}
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={this.handleInputChange}
                                    onFocus={this.onFocus}
                                />
                                {error.email && <label htmlFor="email">{error.email}</label>}
                            </div>
                            <div
                                className={classNames("ReserveVehicleForm__form-section-input phone", {
                                    error: !!error.phone,
                                })}
                            >
                                <MaskedInput
                                    type="tel"
                                    placeholder={t(`${TRANSLATION_KEY}.phone`)}
                                    mask={[
                                        "(",
                                        /[1-9]/,
                                        /\d/,
                                        /\d/,
                                        ")",
                                        " ",
                                        /\d/,
                                        /\d/,
                                        /\d/,
                                        "-",
                                        /\d/,
                                        /\d/,
                                        /\d/,
                                        /\d/,
                                    ]}
                                    guide={false}
                                    name="phone"
                                    value={phone}
                                    onChange={this.handleInputChange}
                                    onFocus={this.onFocus}
                                />
                                {error.phone && <label htmlFor="phone">{error.phone}</label>}
                            </div>
                        </div>

                        <div className="ReserveVehicleForm__form-section">
                            <div
                                className={`ReserveVehicleForm__form-section-input comments ${
                                    charactersExceed && "error"
                                }`}
                            >
                                <label>{t(`${TRANSLATION_KEY}.comments`)}</label>
                                <textarea
                                    name="comments"
                                    value={comments}
                                    placeholder={t(`${TRANSLATION_KEY}.commentsPlaceholder`)}
                                    onChange={this.handleInputChange}
                                    onFocus={this.onFocus}
                                />
                                <small>
                                    {`${comments.length} / ${CHARACTER_LIMIT_FOR_COMMENTS} `}
                                    {t(`${TRANSLATION_KEY}.characters`)}
                                </small>
                            </div>
                        </div>
                    </div>
                </MessageModal>
            </div>
        );
    }

    private handleInputChange = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target = event.target as HTMLInputElement | HTMLTextAreaElement;
        const value: string = target.value;
        const name: string = target.name;
        this.setState({
            formData: { ...this.state.formData, [name]: value },
        } as any);
    };

    private submit = (): Promise<boolean> => {
        if (this.isValid()) {
            analyticsFEA.engine.reserveVehicleFormFinish(this.props.dealId, [this.props.vehicle]);
            return this.props.onSubmit(this.state.formData);
        }
        return new Promise<boolean>((resolve) => resolve(false));
    };

    private onFocus = () => {
        analyticsFEA.engine.reserveVehicleFormStart(this.props.dealId, [this.props.vehicle]);
    };

    private isValid = (): boolean => {
        const { region, t } = this.props;
        const {
            formData: { firstName, lastName, email, phone, optInTransactional },
        } = this.state;
        const e: { [index: string]: string } = {};

        if (firstName === "") {
            e.firstName = t(`${TRANSLATION_KEY}.firstNameEmptyErrorMsg`);
        }

        if (lastName === "") {
            e.lastName = t(`${TRANSLATION_KEY}.lastNameEmptyErrorMsg`);
        }

        if (phone === "" && email === "") {
            e.phone = t(`${TRANSLATION_KEY}.phoneEmptyErrorMsg`);
            e.email = t(`${TRANSLATION_KEY}.emailEmptyErrorMsg`);
        }

        if (validateRequired(phone) && !validatePhone(phone)) {
            e.phone = t(`${TRANSLATION_KEY}.phoneInvalidErrorMsg`);
        }

        if (validateRequired(email) && !validateEmail(email)) {
            e.email = t(`${TRANSLATION_KEY}.emailInvalidErrorMsg`);
        }

        if (!isUS(region)) {
            if (!optInTransactional) {
                e.optInTransactional = t(`${TRANSLATION_KEY}.optInTransactionalRequiredErrorMsg`);
            }
        }

        if (!isEmpty(e)) {
            this.setState({ error: e });
            return false;
        } else {
            this.setState({ error: {} });
            return true;
        }
    };
}

export default withTranslation()(ReserveVehicleForm);
